import React, { useMemo } from "react";
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarDensitySelector,
  GridToolbarExport,
  GridToolbarQuickFilter,
} from "@mui/x-data-grid";
import { getAdminStatus, getFormattedAdminStatus } from "../helpers";
import { Box, IconButton, styled, Tooltip, Typography } from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import {
  getFormattedMasjidStatus,
  getStatus,
} from "../../PageComponents/MasjidTable/Helper";
import { redirectToVanillaPortal } from "../../../helpers/HelperFunction";
import { EntityTableAvatar } from "../../Shared/EntityTableAvatar/EntityTableAvatar";
import EditIcon from "@mui/icons-material/Edit";
import moment from "moment";
import { AdminInterFace, Masjid } from "../../../../Types";
import {
  formatRbacRoleLabel,
  getExternalAdminRoleLabel,
  summarizePermissions,
} from "../../../helpers/rbacExternalAdmin";

const StyledGridOverlay = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  height: "100%",
  "& .no-rows-primary": {
    fill: "#3D4751",
    ...theme.applyStyles("light", {
      fill: "#AEB8C2",
    }),
  },
  "& .no-rows-secondary": {
    fill: "#1D2126",
    ...theme.applyStyles("light", {
      fill: "#E8EAED",
    }),
  },
}));
function CustomNoRowsOverlay() {
  return (
    <StyledGridOverlay>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        width={96}
        viewBox="0 0 452 257"
        aria-hidden
        focusable="false"
      >
        <path
          className="no-rows-primary"
          d="M348 69c-46.392 0-84 37.608-84 84s37.608 84 84 84 84-37.608 84-84-37.608-84-84-84Zm-104 84c0-57.438 46.562-104 104-104s104 46.562 104 104-46.562 104-104 104-104-46.562-104-104Z"
        />
        <path
          className="no-rows-primary"
          d="M308.929 113.929c3.905-3.905 10.237-3.905 14.142 0l63.64 63.64c3.905 3.905 3.905 10.236 0 14.142-3.906 3.905-10.237 3.905-14.142 0l-63.64-63.64c-3.905-3.905-3.905-10.237 0-14.142Z"
        />
        <path
          className="no-rows-primary"
          d="M308.929 191.711c-3.905-3.906-3.905-10.237 0-14.142l63.64-63.64c3.905-3.905 10.236-3.905 14.142 0 3.905 3.905 3.905 10.237 0 14.142l-63.64 63.64c-3.905 3.905-10.237 3.905-14.142 0Z"
        />
        <path
          className="no-rows-secondary"
          d="M0 10C0 4.477 4.477 0 10 0h380c5.523 0 10 4.477 10 10s-4.477 10-10 10H10C4.477 20 0 15.523 0 10ZM0 59c0-5.523 4.477-10 10-10h231c5.523 0 10 4.477 10 10s-4.477 10-10 10H10C4.477 69 0 64.523 0 59ZM0 106c0-5.523 4.477-10 10-10h203c5.523 0 10 4.477 10 10s-4.477 10-10 10H10c-5.523 0-10-4.477-10-10ZM0 153c0-5.523 4.477-10 10-10h195.5c5.523 0 10 4.477 10 10s-4.477 10-10 10H10c-5.523 0-10-4.477-10-10ZM0 200c0-5.523 4.477-10 10-10h203c5.523 0 10 4.477 10 10s-4.477 10-10 10H10c-5.523 0-10-4.477-10-10ZM0 247c0-5.523 4.477-10 10-10h231c5.523 0 10 4.477 10 10s-4.477 10-10 10H10c-5.523 0-10-4.477-10-10Z"
        />
      </svg>
      <Box sx={{ mt: 2 }}>No rows</Box>
    </StyledGridOverlay>
  );
}
type ExternalTableToolbarProps = {
  onOpenAdvancedFilter?: () => void;
  hasActiveFilter?: boolean;
};

const ExternalTableToolbar: React.FC<ExternalTableToolbarProps> = ({
  onOpenAdvancedFilter,
  hasActiveFilter,
}) => (
  <GridToolbarContainer>
    <GridToolbarColumnsButton />
    <GridToolbarDensitySelector />
    <GridToolbarExport />
    <Box sx={{ flexGrow: 1 }} />
    <GridToolbarQuickFilter debounceMs={500} />
    {onOpenAdvancedFilter && (
      <Tooltip title="Advanced Filter">
        <IconButton
          onClick={onOpenAdvancedFilter}
          color={hasActiveFilter ? "primary" : "default"}
          data-testid="FilterListIcon"
          size="small"
          sx={{ ml: 1 }}
          aria-label="Advanced Filter"
        >
          <FilterListIcon />
          <Typography variant="body2" sx={{ ml: 0.5, display: { xs: "none", sm: "inline-flex" } }}>
            Advanced Filter
          </Typography>
        </IconButton>
      </Tooltip>
    )}
  </GridToolbarContainer>
);

interface ExternalTableProps {
  loading: boolean;
  filteredData: AdminInterFace[] | [];
  handleActionClick: (action: any, details: AdminInterFace) => void;
  handleActionClickForMasjid: (entityType: string, details: any) => void;
  onOpenAdvancedFilter?: () => void;
  hasActiveFilter?: boolean;
  /** When true (Sub-Admin tab only), show RBAC role + permissions columns. */
  showRbacColumns?: boolean;
  /** Sub-Admin tab: hide Contact, RBAC role, and Access (permissions) columns. */
  subAdminTab?: boolean;
}
const ExternalTable = ({
  loading,
  filteredData,
  handleActionClick,
  handleActionClickForMasjid,
  onOpenAdvancedFilter,
  hasActiveFilter = false,
  showRbacColumns = false,
  subAdminTab = false,
}: ExternalTableProps) => {
  const getAvatarSrc = (row: AdminInterFace): string | undefined => {
    if (row.ownershipType === "INDIVIDUAL") {
      return (
        row.individualDetails?.[0]?.profilePhoto || row.profilePhoto || undefined
      );
    }
    if (row.ownershipType === "ORGANIZATION") {
      return (
        row.organizationDetails?.[0]?.profilePhoto || row.profilePhoto || undefined
      );
    }
    return row.masjidDetails?.[0]?.masjidProfilePhoto || row.profilePhoto || undefined;
  };

  /** Avoid `flex` on many columns — it splits viewport width equally and causes severe truncation. */
  const stringCellSx = {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    display: "block",
    maxWidth: "100%",
  } as const;

  const columns = useMemo(
    () => [
      {
        field: "name",
        headerName: "Name",
        minWidth: 200,
        width: 200,
        renderCell: (params: any) => {
          const row = params.row as AdminInterFace;
          const name = row.name || "";
          return (
            <Tooltip title={name} placement="top" enterDelay={500}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  minWidth: 0,
                  width: "100%",
                }}
              >
                <EntityTableAvatar src={getAvatarSrc(row)} name={row.name} />
                <Typography variant="body2" sx={stringCellSx}>
                  {name}
                </Typography>
              </Box>
            </Tooltip>
          );
        },
      },
      {
        field: "email",
        headerName: "Email",
        minWidth: 240,
        width: 240,
        renderCell: (params: any) => {
          const v = String(params.value ?? "");
          return (
            <Tooltip title={v} placement="top" enterDelay={400}>
              <Typography variant="body2" sx={stringCellSx}>
                {v}
              </Typography>
            </Tooltip>
          );
        },
      },
      ...(!subAdminTab
        ? [
            {
              field: "contact",
              headerName: "Contact",
              minWidth: 130,
              width: 130,
              renderCell: (params: any) => {
                const v = String(params.value ?? "");
                return (
                  <Tooltip title={v} placement="top" enterDelay={400}>
                    <Typography variant="body2" sx={stringCellSx}>
                      {v || "—"}
                    </Typography>
                  </Tooltip>
                );
              },
            },
          ]
        : []),
      {
        field: "role",
        headerName: "Role",
        minWidth: 140,
        width: 140,
        valueGetter: (params: any) =>
          getExternalAdminRoleLabel(params.row as AdminInterFace),
        renderCell: (params: any) => {
          const v = String(params.value ?? "");
          return (
            <Tooltip title={v} placement="top" enterDelay={400}>
              <Typography variant="body2" sx={stringCellSx}>
                {v}
              </Typography>
            </Tooltip>
          );
        },
      },
      ...(showRbacColumns && !subAdminTab
        ? [
            {
              field: "rbacRole",
              headerName: "RBAC role",
              minWidth: 180,
              width: 190,
              valueGetter: (params: any) =>
                formatRbacRoleLabel(params.row as AdminInterFace),
              renderCell: (params: any) => {
                const v = String(params.value ?? "");
                return (
                  <Tooltip title={v} placement="top" enterDelay={400}>
                    <Typography variant="body2" sx={stringCellSx}>
                      {v}
                    </Typography>
                  </Tooltip>
                );
              },
            },
            {
              field: "permissionsSummary",
              headerName: "Access (permissions)",
              minWidth: 260,
              width: 280,
              sortable: false,
              renderCell: (params: any) => {
                const row = params.row as AdminInterFace;
                const { summary, full } = summarizePermissions(row.permissions);
                if (!full || summary === "—") {
                  return (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={stringCellSx}
                    >
                      {summary}
                    </Typography>
                  );
                }
                return (
                  <Tooltip title={full} placement="top" enterDelay={400}>
                    <Typography
                      variant="body2"
                      sx={{ ...stringCellSx, cursor: "help" }}
                    >
                      {summary}
                    </Typography>
                  </Tooltip>
                );
              },
            },
          ]
        : []),
      {
        field: "ownershipType",
        headerName: "Entity Type",
        minWidth: 120,
        width: 120,
        valueGetter: (params: any) => {
          const value = params.row.ownershipType;
          if (!value) return "N/A";
          return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
        },
      },
      {
        field: "isAddedFromAdmin",
        headerName: "Added By",
        minWidth: 130,
        width: 130,
        valueGetter: (params: any) => {
          const value = params.row.isAddedFromAdmin;
          return value ? "Internal Admin" : "Sign Up";
        },
      },
      {
        field: "status",
        headerName: "Account Status",
        minWidth: 130,
        width: 130,
        valueGetter: (params: any) => {
          const value = getFormattedAdminStatus(
            getAdminStatus(params.row.isFreezed, params.row.isVerified)
          );
          return value;
        },
        renderCell: (params: any) => {
          let color;
          const value = getAdminStatus(
            params.row.isFreezed,
            params.row.isVerified
          );
          // Check the role and set the color accordingly
          if (value === "approved" || value === null) {
            color = "#1B8368";
          } else if (value === "pending") {
            color = "#EDA920";
          } else {
            color = "#FF4949";
          }
          return (
            <Typography
              style={{
                color,
                cursor: "pointer",
                textTransform: "capitalize",
              }}
              variant="body2"
              onClick={() => {
                handleActionClick(value, params.row);
              }}
            >
              {getFormattedAdminStatus(
                getAdminStatus(params.row.isFreezed, params.row.isVerified)
              )}
            </Typography>
          );
        },
      },
      {
        field: "actions",
        headerName: "Actions",
        minWidth: 72,
        width: 72,
        maxWidth: 72,
        align: "center" as const,
        headerAlign: "center" as const,
        sortable: false,
        disableReorder: true,
        renderCell: (params: any) => {
          const value = getAdminStatus(
            params.row.isFreezed,
            params.row.isVerified
          );
          return (
            <IconButton
              size="small"
              aria-label="Edit admin"
              onClick={() => {
                handleActionClick(value, params.row);
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          );
        },
      },
      {
        field: "entity",
        headerName: "Entity Assigned",
        minWidth: 280,
        width: 300,
        valueGetter: (params: any) => {
          const ownershipType = params.row?.ownershipType;
          if (ownershipType === "ORGANIZATION") {
            const hasOrg =
              params.row?.organizationDetails &&
              params.row.organizationDetails.length > 0;
            const orgName = hasOrg
              ? params.row.organizationDetails[0]?.name
              : "";
            return !orgName
              ? ""
              : !params.row?.isApproved
              ? `Pending (${orgName})`
              : orgName;
          } else if (ownershipType === "INDIVIDUAL") {
            const hasInd =
              params.row?.individualDetails &&
              params.row.individualDetails.length > 0;
            const indName = hasInd ? params.row.individualDetails[0]?.name : "";
            return !indName
              ? ""
              : !params.row?.isApproved
              ? `Pending (${indName})`
              : indName;
          } else {
            const hasMasjid =
              params.row?.masjidDetails && params.row.masjidDetails.length > 0;
            const masjidName = hasMasjid
              ? params.row.masjidDetails[0]?.masjidName
              : "";
            return !masjidName
              ? ""
              : !params.row?.isApproved
              ? `Pending (${masjidName})`
              : masjidName;
          }
        },
        renderCell: (params: any) => {
          const ownershipType = params.row?.ownershipType;
          const statusColor =
            !params.row?.isApproved && !params.row?.isAddedFromAdmin
              ? "#F7A400"
              : "#1B8368";
          const linkStyle = { color: "inherit" } as React.CSSProperties;

          const renderLink = (
            id: string,
            name: string,
            ownerType: "MASJID" | "ORGANIZATION" | "INDIVIDUAL"
          ) => (
            <Typography
              onClick={() => {
                redirectToVanillaPortal(id, ownerType);
              }}
              style={{
                ...linkStyle,
                cursor: "pointer",
                textDecoration: "underline",
              }}
            >
              {name}
            </Typography>
          );

          const entityLabel = (() => {
            if (ownershipType === "ORGANIZATION") {
                  const hasOrg =
                    params.row?.organizationDetails &&
                    params.row.organizationDetails.length > 0;
                  const name = hasOrg
                    ? params.row.organizationDetails[0]?.name
                    : "";
                  const id = hasOrg
                    ? params.row.organizationDetails[0]?._id
                    : "";
                  if (!name) return "";
                  return !params.row?.isApproved &&
                    !params.row?.isAddedFromAdmin
                    ? renderLink(id, `Pending (${name})`, "ORGANIZATION")
                    : renderLink(id, name, "ORGANIZATION");
                } else if (ownershipType === "INDIVIDUAL") {
                  const hasInd =
                    params.row?.individualDetails &&
                    params.row.individualDetails.length > 0;
                  const name = hasInd
                    ? params.row.individualDetails[0]?.name
                    : "";
                  const id = hasInd ? params.row.individualDetails[0]?._id : "";
                  if (!name) return "";
                  return !params.row?.isApproved &&
                    !params.row?.isAddedFromAdmin
                    ? renderLink(id, `Pending (${name})`, "INDIVIDUAL")
                    : renderLink(id, name, "INDIVIDUAL");
                } else {
                  const hasMasjid =
                    params.row?.masjidDetails &&
                    params.row.masjidDetails.length > 0;
                  const name = hasMasjid
                    ? params.row.masjidDetails[0]?.masjidName
                    : "";
                  const id = hasMasjid ? params.row.masjidDetails[0]?._id : "";
                  if (!name) return "";
                  return !params.row?.isApproved &&
                    !params.row?.isAddedFromAdmin
                    ? renderLink(id, `Pending (${name})`, "MASJID")
                    : renderLink(id, name, "MASJID");
                }
          })();

          const tooltipTitle = String(params.value ?? "");

          return (
            <Tooltip title={tooltipTitle} placement="top" enterDelay={500}>
              <Box
                sx={{
                  color: statusColor,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: "100%",
                  textTransform: "capitalize",
                }}
              >
                {entityLabel}
              </Box>
            </Tooltip>
          );
        },
      },
      {
        field: "masjidstatus",
        headerName: "Verification Status",
        minWidth: 130,
        width: 130,
        valueGetter: (params: any) => {
          const ownershipType = params.row?.ownershipType;
          if (ownershipType === "ORGANIZATION") {
            const has = params.row?.organizationDetails?.length > 0;
            if (!has) return "";
            const v = getStatus(
              params.row.organizationDetails[0]?.isFreezed,
              params.row.organizationDetails[0]?.isVerified
            );
            return getFormattedMasjidStatus(v);
          } else if (ownershipType === "INDIVIDUAL") {
            const has = params.row?.individualDetails?.length > 0;
            if (!has) return "";
            const v = getStatus(
              params.row.individualDetails[0]?.isFreezed,
              params.row.individualDetails[0]?.isVerified
            );
            return getFormattedMasjidStatus(v);
          } else {
            const has = params.row?.masjidDetails?.length > 0;
            if (!has) return "";
            const v = getStatus(
              params.row.masjidDetails[0]?.isFreezed,
              params.row.masjidDetails[0]?.isVerified
            );
            return getFormattedMasjidStatus(v);
          }
        },
        renderCell: (params: any) => {
          const ownershipType = params.row?.ownershipType;
          const getEntity = () => {
            if (ownershipType === "ORGANIZATION") {
              return params.row?.organizationDetails?.[0];
            } else if (ownershipType === "INDIVIDUAL") {
              return params.row?.individualDetails?.[0];
            }
            return params.row?.masjidDetails?.[0];
          };

          const entity = getEntity();
          if (!entity) return null;

          let color;
          const value = getStatus(entity?.isFreezed, entity?.isVerified);
          // Check the role and set the color accordingly
          if (value === "approved" || value === null) {
            color = "#1B8368";
          } else if (value === "pending") {
            color = "#EDA920";
          } else {
            color = "#FF4949";
          }
          return (
            <Typography
              style={{
                color,
                cursor: "pointer",
                textTransform: "capitalize",
              }}
              variant="body2"
              onClick={() => {
                handleActionClickForMasjid(ownershipType || "MASJID", entity);
              }}
            >
              {getFormattedMasjidStatus(value)}
            </Typography>
          );
        },
      },
      {
        field: "masjidaddress",
        headerName: "Address",
        minWidth: 220,
        width: 220,
        hideable: true,
        valueGetter: (params: any) => {
          const hasMasjidDetails =
            params.row?.masjidDetails && params.row.masjidDetails.length > 0;
          const value = hasMasjidDetails
            ? params.row.masjidDetails[0]?.address ?? ""
            : "";
          return value;
        },
        renderCell: (params: any) => {
          const v = String(params.value ?? "");
          return (
            <Tooltip title={v} placement="top" enterDelay={400}>
              <Typography variant="body2" sx={stringCellSx}>
                {v || "—"}
              </Typography>
            </Tooltip>
          );
        },
      },
      {
        field: "createdAt",
        headerName: "Created At",
        minWidth: 170,
        width: 170,
        hideable: true,
        hide: true,
        valueGetter: (params: any) => {
          const value = params.row.createdAt;
          return moment.utc(value).local().format("DD-MMM-YYYY HH:mm A");
        },
      },
    ],
    [showRbacColumns, subAdminTab, handleActionClick, handleActionClickForMasjid]
  );
  return (
    <DataGrid
      style={{ backgroundColor: "white", minHeight: 480 }}
      loading={loading}
      rows={filteredData}
      columns={columns}
      pageSize={20}
      rowsPerPageOptions={[20]}
      components={{
        Toolbar: ExternalTableToolbar,
        NoRowsOverlay: CustomNoRowsOverlay,
      }}
      componentsProps={{
        toolbar: {
          onOpenAdvancedFilter,
          hasActiveFilter,
        },
      }}
      disableExtendRowFullWidth
      disableSelectionOnClick
      getRowId={(row) => row._id}
      // getRowClassName={params =>
      //   params.indexRelativeToCurrentPage % 2 === 0
      //     ? "even-row"
      //     : "odd-row"}
      getRowClassName={(params) => {
        const status = params?.row
          ? getAdminStatus(params.row.isFreezed, params.row.isVerified)
          : "";
        return status === "rejected" ? "frozen-masjid-row" : "";
      }}
      sx={{
        width: "100%",
        "& .MuiDataGrid-virtualScroller": {
          overflowX: "auto",
          "&::-webkit-scrollbar": {
            width: "8px",
            height: "8px",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "#f0f0f0",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#bfbfbf",
            borderRadius: "8px",
          },
          "&:hover::-webkit-scrollbar-thumb": {
            backgroundColor: "#8f8f8f",
          },
        },
        "& .MuiDataGrid-columnHeaders": {
          minHeight: "48px !important",
        },
        "& .MuiDataGrid-columnHeaderTitle": {
          fontWeight: 600,
          overflow: "visible",
          textOverflow: "clip",
        },
        "& .MuiDataGrid-cell": {
          py: 1,
          userSelect: "text",
          "&:hover": {
            outline: "none",
          },
        },
        "& .MuiDataGrid-cell:focus": {
          outline: "none",
        },
        "& .MuiDataGrid-cell:focus-within": {
          outline: "none",
        },
      }}
    />
  );
};

export default ExternalTable;
